import { useState, useEffect, useMemo } from 'react'
import { supabase } from '../api/supabase'
import type { DeclarationStructuredRow } from '../hooks/useECustoms'

// Z73.22: field-level view/edit of a generated v1.8.3 declaration, driven entirely by the real
// declaration_row_schema metadata (required/cds_length/ucc_data_element) rather than any
// hand-authored per-field spec -- the schema already carries everything needed for the Req/Max/
// UCC DE columns, so this just renders it against the actual generated values.
//
// Pain point this solves (Gary): staff submitted the flat CSV blind, Descartes rejected it on a
// field-position error ("Field 22 max 1 but we sent 11 chars"), and there was no way to see
// which field/position that was without opening the file and counting commas by hand.
//
// Scope: client-side inspect + edit + download only. Edits here do NOT persist back to
// ecustoms_submissions or the source CI data -- same "download-only, not yet wired to a live
// submit" boundary the v1.8.3 generator itself already has (see cdsDeclarationBuilder.ts's
// header comment). A save-back pipeline for 10 row types would be a substantial second feature;
// out of scope for this pass.
//
// Also NOT implemented: a "locked, system-fixed field" distinction (Gary's mockup greys out
// LineType/ClientRef/MessageType specifically). There's no metadata anywhere marking which
// fields are hardcoded-by-the-generator vs. job-derived vs. genuinely free-entry, and hand-
// authoring that classification per field would reintroduce exactly the kind of drift-prone
// hardcoded spec this feature is meant to replace. Every field except LINETYPE (which IS
// structurally fixed -- it's just the row type, shown as a literal) is editable here.

interface SchemaFieldMeta {
  row_type: string
  position: number
  field_name: string
  field_slug: string
  required: string | null // 'M' | '-'
  cds_length: string | null
  ucc_data_element: string | null
  notes: string | null
}

const ROW_TYPE_LABELS: Record<string, string> = {
  AA: 'Header Row',
  HG: 'Header Guarantee',
  HY: 'Header Parties',
  HC: 'Header Container',
  DD: 'Detail Goods',
  DP: 'Detail Package',
  DA: 'Detail Additional Info',
  DO: 'Detail Documents',
  DT: 'Detail Tax Lines',
  NA: 'Names & Addresses',
}

// Row type display order -- matches the generator's own emission order (AA, HG, HY, HC, then
// per-item DD/DP/DA/DO/DT), NA last since it's only ever inline/optional.
const ROW_TYPE_ORDER = ['AA', 'HG', 'HY', 'HC', 'DD', 'DP', 'DA', 'DO', 'DT', 'NA']

function csvEscape(v: string): string {
  return /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v
}

// cds_length is stored as text, e.g. "16.2" (16 total, 2 decimal per Gary's spec), "1", "126" --
// take the leading integer for a simple length-of-string check. A field with no parseable length
// (rare) just never shows the length warning, rather than a guessed cap.
function parseMaxLen(cdsLength: string | null): number | null {
  if (!cdsLength) return null
  const m = cdsLength.match(/^(\d+)/)
  return m ? parseInt(m[1], 10) : null
}

function isNotUsed(required: string | null, notes: string | null): boolean {
  if (required !== '-') return false
  return /not used|not required/i.test(notes || '')
}

function buildCsvLine(rowType: string, fields: SchemaFieldMeta[], getValue: (slug: string) => string): string {
  const ordered = [...fields].sort((a, b) => a.position - b.position)
  const cells = ordered.map(f => (f.field_slug === 'LINETYPE' ? rowType : csvEscape(getValue(f.field_slug))))
  let lastNonBlank = 0
  cells.forEach((c, i) => { if (c !== '') lastNonBlank = i })
  return cells.slice(0, lastNonBlank + 1).join(',')
}

function downloadCsv(filename: string, content: string) {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

interface RowInstance {
  globalIndex: number
  row: DeclarationStructuredRow
}

function RowInstanceTable({
  rowType, instance, fields, edited, onEdit,
}: {
  rowType: string
  instance: RowInstance
  fields: SchemaFieldMeta[]
  edited: Record<string, string> | undefined
  onEdit: (globalIndex: number, slug: string, value: string) => void
}) {
  const getValue = (slug: string) => edited?.[slug] ?? instance.row.values[slug] ?? ''
  const ordered = useMemo(() => [...fields].sort((a, b) => a.position - b.position), [fields])
  const csvLine = buildCsvLine(rowType, fields, getValue)

  const copyRow = async () => {
    try {
      await navigator.clipboard.writeText(csvLine)
    } catch {
      // clipboard API can be denied in some contexts -- the raw line is already shown below, so
      // silently failing to auto-copy isn't a dead end, just a missed convenience.
    }
  }

  return (
    <div className="mb-4 last:mb-0">
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-semibold text-gray-600 dark:text-gray-300">{instance.row.label}</span>
        <button onClick={copyRow} className="text-xs text-blue-600 dark:text-blue-400 hover:underline">Copy Row</button>
      </div>
      <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
        <table className="w-full text-xs">
          <thead className="bg-gray-50 dark:bg-gray-700/50">
            <tr>
              <th className="px-2 py-1.5 text-left font-medium text-gray-500 dark:text-gray-400 w-10">Pos</th>
              <th className="px-2 py-1.5 text-left font-medium text-gray-500 dark:text-gray-400">Field Name</th>
              <th className="px-2 py-1.5 text-left font-medium text-gray-500 dark:text-gray-400 w-10">Req</th>
              <th className="px-2 py-1.5 text-left font-medium text-gray-500 dark:text-gray-400 w-12">Max</th>
              <th className="px-2 py-1.5 text-left font-medium text-gray-500 dark:text-gray-400">Value</th>
              <th className="px-2 py-1.5 text-left font-medium text-gray-500 dark:text-gray-400 w-16">UCC DE</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {ordered.map(f => {
              if (f.field_slug === 'LINETYPE') {
                return (
                  <tr key={f.position} className="bg-gray-50 dark:bg-gray-900/40">
                    <td className="px-2 py-1 text-gray-400">{f.position}</td>
                    <td className="px-2 py-1 text-gray-500 dark:text-gray-400">{f.field_name}</td>
                    <td className="px-2 py-1 text-gray-400">M</td>
                    <td className="px-2 py-1 text-gray-400">{f.cds_length}</td>
                    <td className="px-2 py-1 font-mono text-gray-500 dark:text-gray-400">{rowType} 🔒</td>
                    <td className="px-2 py-1 text-gray-400">N/A</td>
                  </tr>
                )
              }
              const notUsed = isNotUsed(f.required, f.notes)
              const value = getValue(f.field_slug)
              const maxLen = parseMaxLen(f.cds_length)
              const tooLong = maxLen != null && value.trim().length > maxLen
              const missingRequired = f.required === 'M' && !value.trim()
              const rowBg = notUsed
                ? 'bg-gray-50 dark:bg-gray-900/40'
                : missingRequired
                  ? 'bg-red-50 dark:bg-red-900/20'
                  : tooLong
                    ? 'bg-amber-50 dark:bg-amber-900/20'
                    : ''
              return (
                <tr key={f.position} className={rowBg} title={f.notes || undefined}>
                  <td className="px-2 py-1 text-gray-400">{f.position}</td>
                  <td className="px-2 py-1 text-gray-700 dark:text-gray-300">{f.field_name}</td>
                  <td className="px-2 py-1 text-gray-500 dark:text-gray-400">{f.required || '-'}</td>
                  <td className="px-2 py-1 text-gray-500 dark:text-gray-400">{f.cds_length || '-'}</td>
                  <td className="px-2 py-1">
                    {notUsed ? (
                      <span className="text-gray-400 italic" title={f.notes || 'Not used'}>Not used</span>
                    ) : (
                      <input
                        type="text"
                        value={value}
                        placeholder=","
                        onChange={e => onEdit(instance.globalIndex, f.field_slug, e.target.value)}
                        className={`w-full px-1.5 py-0.5 text-xs border rounded bg-white dark:bg-gray-800 dark:text-white font-mono ${
                          missingRequired ? 'border-red-400 dark:border-red-600' : tooLong ? 'border-amber-400 dark:border-amber-600' : 'border-gray-300 dark:border-gray-600'
                        }`}
                      />
                    )}
                    {missingRequired && <span className="block text-red-600 dark:text-red-400 mt-0.5">REQUIRED</span>}
                    {tooLong && <span className="block text-amber-600 dark:text-amber-400 mt-0.5">⚠ Descartes max {maxLen}, current {value.trim().length}</span>}
                  </td>
                  <td className="px-2 py-1 text-gray-400 font-mono">{f.ucc_data_element && f.ucc_data_element !== 'N/A' ? f.ucc_data_element : '—'}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      <p className="mt-1 px-1 text-[11px] font-mono text-gray-400 dark:text-gray-500 truncate" title={csvLine}>{csvLine || '(blank row)'}</p>
    </div>
  )
}

function RowTypeSection({
  rowType, instances, fields, edited, onEdit,
}: {
  rowType: string
  instances: RowInstance[]
  fields: SchemaFieldMeta[]
  edited: Record<number, Record<string, string>>
  onEdit: (globalIndex: number, slug: string, value: string) => void
}) {
  const [open, setOpen] = useState(false)
  const label = ROW_TYPE_LABELS[rowType] || rowType

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg mb-3 overflow-hidden">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 dark:bg-gray-700/50 text-left"
      >
        <span className="text-sm font-medium text-gray-800 dark:text-gray-200">
          {open ? '▼' : '▶'} {rowType} {label} ({fields.length} fields{instances.length > 1 ? ` × ${instances.length}` : ''})
        </span>
      </button>
      {open && (
        <div className="p-3">
          {instances.map(inst => (
            <RowInstanceTable
              key={inst.globalIndex}
              rowType={rowType}
              instance={inst}
              fields={fields}
              edited={edited[inst.globalIndex]}
              onEdit={onEdit}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export default function RawCsvEditor({
  structuredRows, filename, onRegenerate, regenerating,
}: {
  structuredRows: DeclarationStructuredRow[]
  filename?: string
  onRegenerate: () => void
  regenerating: boolean
}) {
  const [schemaByType, setSchemaByType] = useState<Record<string, SchemaFieldMeta[]> | null>(null)
  const [schemaError, setSchemaError] = useState<string | null>(null)
  const [edited, setEdited] = useState<Record<number, Record<string, string>>>({})

  useEffect(() => {
    supabase
      .from('declaration_row_schema')
      .select('row_type, position, field_name, field_slug, required, cds_length, ucc_data_element, notes')
      .eq('spec_version', 'v1.8.3')
      .order('position', { ascending: true })
      .then(({ data, error }) => {
        if (error) { setSchemaError(error.message); return }
        const grouped: Record<string, SchemaFieldMeta[]> = {}
        for (const f of (data || []) as SchemaFieldMeta[]) {
          if (!grouped[f.row_type]) grouped[f.row_type] = []
          grouped[f.row_type].push(f)
        }
        setSchemaByType(grouped)
      })
  }, [])

  // Reset edits whenever a fresh generation comes in -- stale edits against a regenerated
  // declaration (different LRN, possibly different line items) would be confusing to carry over.
  useEffect(() => { setEdited({}) }, [structuredRows])

  const instancesByType = useMemo(() => {
    const m: Record<string, RowInstance[]> = {}
    structuredRows.forEach((row, globalIndex) => {
      if (!m[row.rowType]) m[row.rowType] = []
      m[row.rowType].push({ globalIndex, row })
    })
    return m
  }, [structuredRows])

  const handleEdit = (globalIndex: number, slug: string, value: string) => {
    setEdited(prev => ({ ...prev, [globalIndex]: { ...prev[globalIndex], [slug]: value } }))
  }

  const handleDownload = () => {
    if (!schemaByType) return
    const lines = structuredRows.map((row, globalIndex) => {
      const fields = schemaByType[row.rowType] || []
      const getValue = (slug: string) => edited[globalIndex]?.[slug] ?? row.values[slug] ?? ''
      return buildCsvLine(row.rowType, fields, getValue)
    })
    downloadCsv(filename || 'declaration_edited.csv', lines.join('\r\n'))
  }

  const editedCellCount = Object.values(edited).reduce((sum, cells) => sum + Object.keys(cells).length, 0)

  if (schemaError) {
    return <p className="text-sm text-red-600 dark:text-red-400">Failed to load field spec: {schemaError}</p>
  }
  if (!schemaByType) {
    return <p className="text-sm text-gray-500 dark:text-gray-400">Loading field spec…</p>
  }

  const presentTypes = ROW_TYPE_ORDER.filter(t => instancesByType[t]?.length)

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Every field from the real Descartes spec, grouped by row type. Grey "Not used" fields
          aren't sent to Descartes at all; red means a required field is empty; amber means the
          value is longer than Descartes allows for that position.
        </p>
        <div className="flex items-center gap-2 flex-shrink-0 ml-3">
          <button onClick={onRegenerate} disabled={regenerating} className="px-3 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50">
            {regenerating ? 'Regenerating…' : 'Regenerate from job data'}
          </button>
          <button onClick={handleDownload} className="px-3 py-1.5 text-xs bg-orange-600 hover:bg-orange-700 text-white rounded-lg font-medium">
            Download Edited CSV{editedCellCount > 0 ? ` (${editedCellCount} edited)` : ''}
          </button>
        </div>
      </div>

      {presentTypes.map(rowType => (
        <RowTypeSection
          key={rowType}
          rowType={rowType}
          instances={instancesByType[rowType]}
          fields={schemaByType[rowType] || []}
          edited={edited}
          onEdit={handleEdit}
        />
      ))}
    </div>
  )
}
